export const PMS_LANGUAGE_STORAGE_KEY = "palantir_pms_language";
export const PMS_DEFAULT_LANGUAGE_ID = "en";
const PMS_LANGUAGE_BY_USER_STORAGE_KEY = "palantir_pms_language_by_user";

const languages = [
  {
    id: "en",
    label: "English",
    nativeLabel: "English",
    locale: "en-GB",
    messages: {
      common: {
        all: "All",
        clear: "Clear",
        logout: "Logout",
        businessUnit: "Business Unit",
        notAvailable: "N/A",
        unassigned: "Unassigned",
      },
      settings: {
        userExperienceTab: "User Experience",
        panelEyebrow: "User Experience",
        panelTitle: "Theme and language",
        panelCopy:
          "Adjust how the PMS feels for each user. Theme changes immediately, and language currently applies to the sidebar and Reservations.",
        appearanceSection: "Appearance",
        appearanceTitle: "Theme mode",
        appearanceCopy:
          "This changes the PMS shell, sidebar and Reservations screens immediately. Pilcore Night uses the deep brand palette; Pilcore Day combines a blue structure with clean white surfaces.",
        languageSection: "Language",
        languageTitle: "Interface language",
        languageCopy:
          "This currently updates the PMS sidebar and the Reservations screens immediately.",
        previewSubheading: "First visual sample of the new theme on the list screen.",
        previewActionNew: "New reservation",
        previewActionExport: "Export",
        previewActionListMode: "List mode",
        note:
          "The change is applied instantly. You do not need to press Save Settings for theme or language.",
      },
      sidebar: {
        pmsPanel: "PMS Panel",
        dashboard: "Dashboard",
        dashboardMeta: "Today, MTD and YTD KPIs",
        reservations: "Reservations",
        reservationsMeta: "List, availability, sales control",
        listOfReservations: "List of Reservations",
        waitlist: "Waitlist",
        reservationsAvailability: "Reservations Availability",
        categorySalesControl: "Category Sales Control",
        reports: "Reports",
        reportsMeta: "Stay performance and KPIs",
        monthlyStayPerformance: "Monthly Stay Performance",
        frontDeskSettings: "Front Desk Settings",
        frontDesk: "Front Desk",
        frontDeskMeta: "Check-ins, in-house, check-outs",
        checkInsToday: "Check-Ins Today",
        inHouseGuests: "In-House Guests",
        checkOutsToday: "Check-Outs Today",
        roomsManagement: "Rooms Management",
        roomsManagementMeta: "Matrix, assignment, blocks",
        roomMatrix: "Room Matrix",
        roomAssignment: "Room Assignment",
        roomBlocks: "Room Blocks",
        revenueManagement: "Revenue Management",
        revenueManagementMeta: "Rates and market intelligence",
        roomRatesManagement: "Room Rates Management",
        roomRatesMatrix: "Room Rates Matrix",
        marketIntelligence: "Market Intelligence",
        revenueOptimizer: "Revenue Optimizer",
        revenueOptimizerSetup: "Setup",
        revenueOptimizerSignalsForecast: "Signals & Forecast",
        revenueOptimizerMarketBenchmark: "Market Benchmark",
        revenueOptimizerRecommendations: "Recommendations",
        marketRulesMapping: "Market Rules & Mapping",
        competitorPriceMonitor: "Competitor Price Monitor",
        internalVsMarketRatePrices: "Internal vs Market Rate Prices",
        reviewIntelligence: "Review Intelligence",
        reviewIntelligenceMeta: "Reviews, follow-up and imports",
        reviewsDashboard: "Reviews Dashboard",
        reviewOperations: "Review Operations",
        trendsActions: "Trends & Actions",
        allReviews: "All Reviews",
        reviewImport: "Review Import",
        guestFeedbackSurveys: "Guest Feedback Surveys",
        settings: "Settings",
        settingsMeta: "Hotel configuration",
        hotelLegalDetails: "Hotel & Legal Details",
        reservationSettings: "Reservation Settings",
        revenueSettings: "Revenue Settings",
        currencies: "Currencies",
        displayCurrency: "Display currency",
        extrasDiscounts: "Extras & Discounts",
        reservationCharges: "Reservation Charges",
        usersRoles: "Users & Roles",
        userExperience: "User Experience",
        bookingEngine: "Booking Engine",
        pinSidebar: "Pin sidebar",
        unpinSidebar: "Unpin sidebar",
      },
      reservations: {
        pageTitle: "Reservations",
        loadedCount: (count) => `${count} reservations loaded`,
        add: "Add",
        exportToExcel: "Export to Excel",
        viewCancelled: "View Cancelled",
        hideCancelled: "Hide Cancelled",
        searchPlaceholder: "Search by locator, customer, room, dates, status...",
        occupancyGrid: "Occupancy Grid",
        filters: "Filters",
        clearAllFilters: "Clear all",
        showingRange: (start, end, total) => `Showing ${start}-${end} of ${total}`,
        size: "size",
        prev: "Prev",
        next: "Next",
        pageCounter: (page, total) => `Page ${page}/${total}`,
        locator: "Locator",
        externalLocator: "External Locator",
        customer: "Customer",
        roomCategory: "Room Category",
        room: "Room",
        rate: "Rate",
        checkIn: "Check-in",
        checkOut: "Check-out",
        roomNights: "Room Nights",
        adults: "Adults",
        children: "Children",
        babies: "Babies",
        reservationStatus: "Reservation Status",
        totalPrice: "Total Price",
        paidAmount: "Paid Amount",
        externalAgency: "External Agency",
        createdAt: "Created at",
        createdBy: "Created by",
        loadingReservations: "Loading reservations...",
        noReservationsFound: "No reservations found.",
        contains: (needle) => `Contains: ${needle}`,
        all: "All",
        categoriesCount: (count) => `${count} categories`,
        roomsCount: (count) => `${count} rooms`,
        ratesCount: (count) => `${count} rates`,
        datesCount: (count) => `${count} dates`,
        statusesCount: (count) => `${count} statuses`,
        agenciesCount: (count) => `${count} agencies`,
        selectedCount: (count) => `${count} selected`,
        textFilterPlaceholder: "Contains...",
        textFilterHint: "Filter by text within this field.",
        searchIn: (title) => `Search in ${String(title || "").toLocaleLowerCase("en-GB")}...`,
        noMatchingOptions: "No matching options.",
        selectValuesHint: "Select one or more values.",
        openReservation: "Open reservation",
        detailsPermissionRequired: "View Reservation details permission required",
        detailsPermissionAlert:
          "Access denied: you need the View Reservation details permission to open this entry.",
        errorCreatingGroup: "Error creating group and first reservation.",
        noDataToExport: "No data to export.",
        groupRatePrefix: "Group Rate",
        booker: "booker",
        workbookSheetName: "Reservations",
        workbookFileName: "reservations.xlsx",
      },
      reservationsTabs: {
        noPermission: "You don't have permission to view reservations.",
        savedSuccess: "Reservation saved successfully",
        listOfReservations: "List of Reservations",
        waitlist: "Waitlist",
        reservationsAvailability: "Reservations Availability",
        categorySalesControl: "Category Sales Control",
      },
      waitlist: {
        title: "Waitlist",
        description:
          "Manual entries watch category availability. Linked entries only trigger when their exact reservation is really cancelled or deleted.",
        newEntry: "New Waitlist Entry",
        selectBusinessUnit: "Select a business unit to manage waitlist entries.",
        searchPlaceholder: "Search guest, category, reservation or status...",
        loading: "Loading waitlist...",
        empty: "No waitlist entries match the current filters.",
        table: {
          guest: "Guest",
          stay: "Stay",
          checkIn: "Check-in",
          checkOut: "Check-out",
          roomCategory: "Room Category",
          linkedReservation: "Linked Reservation",
          status: "Status",
          type: "Type",
          actions: "Actions",
        },
        entryTypes: {
          manual: "Manual",
          reservation_linked: "Reservation linked",
        },
        statusFilters: {
          all: "All",
          pending: "Waiting",
          blocked: "Availability secured",
          converted: "Reservation created",
          cancelled: "Removed",
          expired: "Expired",
        },
        statuses: {
          Pending: "Waiting",
          Blocked: "Availability secured",
          Converted: "Reservation created",
          Cancelled: "Removed",
          Expired: "Expired",
        },
        actions: {
          createReservation: "Create reservation",
          delete: "Remove from waitlist",
          removeShort: "Remove",
          retry: "Retry",
          converted: "Reservation created",
          close: "Close",
          cancel: "Cancel",
          select: "Select",
          changeReservation: "Change reservation",
        },
        modal: {
          title: "Create Waitlist Entry",
          copy:
            "Manual entries reserve a category when enough space opens. Linked entries wait for one exact reservation to be cancelled or deleted.",
          waitlistType: "Waitlist Type",
          notes: "Notes",
          notesPlaceholder: "Optional internal note...",
          guestName: "Guest Name",
          guestNamePlaceholder: "Guest full name",
          roomCategory: "Room Category",
          selectCategory: "Select a category",
          loadingCategories: "Loading categories...",
          noCategories: "No room categories are available.",
          categoryLoadError: "Room categories could not be loaded.",
          checkIn: "Check-in",
          checkOut: "Check-out",
          email: "Email",
          emailPlaceholder: "Optional email",
          phone: "Phone",
          phonePlaceholder: "Optional phone",
          searchReservation: "Search Reservation",
          searchReservationPlaceholder: "Search by guest name, email or phone...",
          searchingReservations: "Searching reservations...",
          noReservations: "No reservations found for that search.",
          searchReservationsError: "Reservations could not be searched. Please try again.",
          dateOrderError: "Check-out must be after check-in.",
          checkInPastError: "Check-in cannot be before today.",
          invalidEmailError: "Enter a valid email address or leave the field empty.",
          completeRequiredFields: "Complete the required fields before creating the entry.",
          create: "Create Waitlist Entry",
          creating: "Creating...",
          removeTitle: "Remove waitlist entry?",
          removeCopy:
            "The entry and any availability hold linked to it will be removed. This action cannot be undone.",
          removeConfirm: "Remove entry",
          removing: "Removing...",
        },
        selection: {
          statusPrefix: "Status",
          roomPrefix: "Room",
          noCategory: "No category",
          noEmail: "No email",
          noPhone: "No phone",
          splitStay: "Split stay: an entry will be created for each stay segment.",
          disabledReasons: {
            noCategory: "Reservation has no room category",
            incompleteDates: "Reservation has incomplete stay dates",
            cancelled: "Reservation is cancelled",
            checkedIn: "Reservation is already checked in",
            alreadyOnWaitlist: "Already on waitlist",
            beforeToday: "Reservation check-in date is before today",
            inPast: "Reservation is in the past",
            draft: "Reservation is still draft",
          },
        },
        messages: {
          loadError: "Error loading waitlist",
          createSuccess: "Waitlist entry created successfully.",
          createError: "Error creating waitlist entry",
          removeSuccess: "Waitlist entry removed.",
          removeError: "Error removing waitlist entry",
          openDraftError: "Error opening reservation draft",
          businessUnitSyncError:
            "The business unit session could not be updated. Return to your active unit and try again.",
          refreshWarning:
            "The action succeeded, but the list could not refresh. The data shown may be outdated.",
          duplicateEntry: "This reservation already has an active waitlist entry.",
          removeStateConflict:
            "Only entries waiting or with availability secured can be removed.",
          openDraftRemovalConflict:
            "This entry is protecting an open reservation draft. Close or confirm the draft before removing it.",
        },
      },
      statuses: {
        confirmed: "Confirmed",
        requested: "Requested",
        cancelled: "Cancelled",
        canceled: "Cancelled",
        checked_in: "Checked-in",
        checked_out: "Checked-out",
        no_show: "No-show",
        in_house: "In house",
        draft: "Draft",
      },
    },
  },
  {
    id: "es",
    label: "Spanish",
    nativeLabel: "Español",
    locale: "es-ES",
    messages: {
      common: {
        all: "Todos",
        clear: "Limpiar",
        logout: "Cerrar sesión",
        businessUnit: "Unidad de negocio",
        notAvailable: "N/D",
        unassigned: "Sin asignar",
      },
      settings: {
        userExperienceTab: "Experiencia de usuario",
        panelEyebrow: "Experiencia de usuario",
        panelTitle: "Tema e idioma",
        panelCopy:
          "Ajusta cómo se siente el PMS para cada usuario. El tema cambia al instante y, por ahora, el idioma se aplica a la sidebar y a Reservations.",
        appearanceSection: "Apariencia",
        appearanceTitle: "Modo de tema",
        appearanceCopy:
          "Esto cambia al instante el shell del PMS, la sidebar y las pantallas de Reservations. Pilcore Night utiliza la paleta profunda de marca; Pilcore Day combina estructura azul y superficies blancas limpias.",
        languageSection: "Idioma",
        languageTitle: "Idioma de la interfaz",
        languageCopy:
          "Por ahora esto actualiza al instante la sidebar del PMS y las pantallas de Reservations.",
        previewSubheading: "Primera muestra visual del nuevo tema en la pantalla de lista.",
        previewActionNew: "Nueva reserva",
        previewActionExport: "Exportar",
        previewActionListMode: "Modo lista",
        note:
          "El cambio se aplica al instante. No necesitas pulsar Guardar Settings para el tema ni el idioma.",
      },
      sidebar: {
        pmsPanel: "Panel PMS",
        dashboard: "Panel",
        dashboardMeta: "KPIs de hoy, MTD y YTD",
        reservations: "Reservas",
        reservationsMeta: "Lista, disponibilidad y control de ventas",
        listOfReservations: "Lista de reservas",
        waitlist: "Lista de espera",
        reservationsAvailability: "Disponibilidad de reservas",
        categorySalesControl: "Control de ventas por categoría",
        reports: "Reportes",
        reportsMeta: "Rendimiento de estancias y KPIs",
        monthlyStayPerformance: "Rendimiento mensual de estancias",
        frontDeskSettings: "Configuración de Front Desk",
        frontDesk: "Recepción",
        frontDeskMeta: "Check-ins, alojados y check-outs",
        checkInsToday: "Check-ins de hoy",
        inHouseGuests: "Huéspedes alojados",
        checkOutsToday: "Check-outs de hoy",
        roomsManagement: "Gestión de habitaciones",
        roomsManagementMeta: "Matriz, asignación y bloqueos",
        roomMatrix: "Matriz de habitaciones",
        roomAssignment: "Asignación de habitaciones",
        roomBlocks: "Bloqueos de habitaciones",
        revenueManagement: "Revenue Management",
        revenueManagementMeta: "Tarifas e inteligencia de mercado",
        roomRatesManagement: "Gestión de tarifas",
        roomRatesMatrix: "Matriz de tarifas",
        marketIntelligence: "Inteligencia de mercado",
        revenueOptimizer: "Revenue Optimizer",
        revenueOptimizerSetup: "Configuración",
        revenueOptimizerSignalsForecast: "Señales y forecast",
        revenueOptimizerMarketBenchmark: "Benchmark de mercado",
        revenueOptimizerRecommendations: "Recomendaciones",
        marketRulesMapping: "Reglas y mapeo de mercado",
        competitorPriceMonitor: "Monitor de precios de la competencia",
        internalVsMarketRatePrices: "Precios internos vs mercado",
        reviewIntelligence: "Inteligencia de reseñas",
        reviewIntelligenceMeta: "Reseñas, seguimiento e importaciones",
        reviewsDashboard: "Panel de reseñas",
        reviewOperations: "Operaciones de reseñas",
        trendsActions: "Tendencias y acciones",
        allReviews: "Todas las reseñas",
        reviewImport: "Importación de reseñas",
        guestFeedbackSurveys: "Encuestas a huéspedes",
        settings: "Ajustes",
        settingsMeta: "Configuración del hotel",
        hotelLegalDetails: "Datos del hotel y entidad legal",
        reservationSettings: "Ajustes de reservas",
        revenueSettings: "Ajustes de revenue",
        currencies: "Monedas",
        displayCurrency: "Moneda mostrada",
        extrasDiscounts: "Extras y descuentos",
        reservationCharges: "Cargos de reservas",
        usersRoles: "Usuarios y roles",
        userExperience: "Experiencia de usuario",
        bookingEngine: "Motor de reservas",
        pinSidebar: "Fijar sidebar",
        unpinSidebar: "Desfijar sidebar",
      },
      reservations: {
        pageTitle: "Reservas",
        loadedCount: (count) => `${count} reservas cargadas`,
        add: "Añadir",
        exportToExcel: "Exportar a Excel",
        viewCancelled: "Ver canceladas",
        hideCancelled: "Ocultar canceladas",
        searchPlaceholder: "Buscar por localizador, cliente, habitación, fechas, estado...",
        occupancyGrid: "Parrilla de ocupación",
        filters: "Filtros",
        clearAllFilters: "Limpiar todos",
        showingRange: (start, end, total) => `Mostrando ${start}-${end} de ${total}`,
        size: "tamaño",
        prev: "Anterior",
        next: "Siguiente",
        pageCounter: (page, total) => `Página ${page}/${total}`,
        locator: "Localizador",
        externalLocator: "Localizador externo",
        customer: "Cliente",
        roomCategory: "Categoría de habitación",
        room: "Habitación",
        rate: "Tarifa",
        checkIn: "Check-in",
        checkOut: "Check-out",
        roomNights: "Noches",
        adults: "Adultos",
        children: "Niños",
        babies: "Bebés",
        reservationStatus: "Estado de la reserva",
        totalPrice: "Precio total",
        paidAmount: "Importe pagado",
        externalAgency: "Agencia externa",
        createdAt: "Creado el",
        createdBy: "Creado por",
        loadingReservations: "Cargando reservas...",
        noReservationsFound: "No se han encontrado reservas.",
        contains: (needle) => `Contiene: ${needle}`,
        all: "Todos",
        categoriesCount: (count) => `${count} categorías`,
        roomsCount: (count) => `${count} habitaciones`,
        ratesCount: (count) => `${count} tarifas`,
        datesCount: (count) => `${count} fechas`,
        statusesCount: (count) => `${count} estados`,
        agenciesCount: (count) => `${count} agencias`,
        selectedCount: (count) => `${count} seleccionados`,
        textFilterPlaceholder: "Contiene...",
        textFilterHint: "Filtra por texto dentro del campo.",
        searchIn: (title) => `Buscar en ${String(title || "").toLocaleLowerCase("es-ES")}...`,
        noMatchingOptions: "No hay opciones que coincidan.",
        selectValuesHint: "Selecciona uno o varios valores.",
        openReservation: "Abrir reserva",
        detailsPermissionRequired: "Se requiere el permiso Ver detalles de la reserva",
        detailsPermissionAlert:
          "Acceso denegado: necesitas el permiso Ver detalles de la reserva para abrir esta entrada.",
        errorCreatingGroup: "Error al crear el grupo y la primera reserva.",
        noDataToExport: "No hay datos para exportar.",
        groupRatePrefix: "Tarifa de grupo",
        booker: "titular",
        workbookSheetName: "Reservas",
        workbookFileName: "reservas.xlsx",
      },
      reservationsTabs: {
        noPermission: "No tienes permiso para ver reservas.",
        savedSuccess: "Reserva guardada correctamente",
        listOfReservations: "Lista de reservas",
        waitlist: "Lista de espera",
        reservationsAvailability: "Disponibilidad de reservas",
        categorySalesControl: "Control de ventas por categoría",
      },
      statuses: {
        confirmed: "Confirmada",
        requested: "Solicitada",
        cancelled: "Cancelada",
        canceled: "Cancelada",
        checked_in: "Check-in realizado",
        checked_out: "Check-out realizado",
        no_show: "No show",
        in_house: "Alojada",
        draft: "Borrador",
      },
    },
  },
  {
    id: "ca",
    label: "Catalan",
    nativeLabel: "Català",
    locale: "ca-ES",
    messages: {
      common: {
        all: "Tots",
        clear: "Neteja",
        logout: "Tanca la sessió",
        businessUnit: "Unitat de negoci",
        notAvailable: "N/D",
        unassigned: "Sense assignar",
      },
      settings: {
        userExperienceTab: "Experiència d'usuari",
        panelEyebrow: "Experiència d'usuari",
        panelTitle: "Tema i idioma",
        panelCopy:
          "Ajusta com es percep el PMS per a cada usuari. El tema canvia a l'instant i, de moment, l'idioma s'aplica a la sidebar i a Reservations.",
        appearanceSection: "Aparença",
        appearanceTitle: "Mode de tema",
        appearanceCopy:
          "Això canvia a l'instant el shell del PMS, la sidebar i les pantalles de Reservations. Pilcore Night utilitza la paleta profunda de marca; Pilcore Day combina estructura blava i superfícies blanques netes.",
        languageSection: "Idioma",
        languageTitle: "Idioma de la interfície",
        languageCopy:
          "De moment això actualitza a l'instant la sidebar del PMS i les pantalles de Reservations.",
        previewSubheading: "Primera mostra visual del nou tema a la pantalla de llista.",
        previewActionNew: "Nova reserva",
        previewActionExport: "Exporta",
        previewActionListMode: "Mode llista",
        note:
          "El canvi s'aplica a l'instant. No cal prémer Desa Settings per al tema ni per a l'idioma.",
      },
      sidebar: {
        pmsPanel: "Panell PMS",
        dashboard: "Panell",
        dashboardMeta: "KPIs d'avui, MTD i YTD",
        reservations: "Reserves",
        reservationsMeta: "Llista, disponibilitat i control de vendes",
        listOfReservations: "Llista de reserves",
        waitlist: "Llista d'espera",
        reservationsAvailability: "Disponibilitat de reserves",
        categorySalesControl: "Control de vendes per categoria",
        reports: "Informes",
        reportsMeta: "Rendiment d'estades i KPIs",
        monthlyStayPerformance: "Rendiment mensual d'estades",
        frontDeskSettings: "Configuracio de Front Desk",
        frontDesk: "Recepció",
        frontDeskMeta: "Check-ins, allotjats i check-outs",
        checkInsToday: "Check-ins d'avui",
        inHouseGuests: "Hostes allotjats",
        checkOutsToday: "Check-outs d'avui",
        roomsManagement: "Gestió d'habitacions",
        roomsManagementMeta: "Matriu, assignació i bloquejos",
        roomMatrix: "Matriu d'habitacions",
        roomAssignment: "Assignació d'habitacions",
        roomBlocks: "Bloquejos d'habitacions",
        revenueManagement: "Revenue Management",
        revenueManagementMeta: "Tarifes i intel·ligència de mercat",
        roomRatesManagement: "Gestió de tarifes",
        roomRatesMatrix: "Matriu de tarifes",
        marketIntelligence: "Intel·ligència de mercat",
        revenueOptimizer: "Revenue Optimizer",
        revenueOptimizerSetup: "Configuració",
        revenueOptimizerSignalsForecast: "Senyals i forecast",
        revenueOptimizerMarketBenchmark: "Benchmark de mercat",
        revenueOptimizerRecommendations: "Recomanacions",
        marketRulesMapping: "Regles i mapatge de mercat",
        competitorPriceMonitor: "Monitor de preus de la competència",
        internalVsMarketRatePrices: "Preus interns vs mercat",
        reviewIntelligence: "Intel·ligència de ressenyes",
        reviewIntelligenceMeta: "Ressenyes, seguiment i importacions",
        reviewsDashboard: "Panell de ressenyes",
        reviewOperations: "Operacions de ressenyes",
        trendsActions: "Tendències i accions",
        allReviews: "Totes les ressenyes",
        reviewImport: "Importació de ressenyes",
        guestFeedbackSurveys: "Enquestes a hostes",
        settings: "Ajustos",
        settingsMeta: "Configuració de l'hotel",
        hotelLegalDetails: "Dades de l'hotel i entitat legal",
        reservationSettings: "Ajustos de reserves",
        revenueSettings: "Ajustos de revenue",
        currencies: "Monedes",
        displayCurrency: "Moneda mostrada",
        extrasDiscounts: "Extres i descomptes",
        reservationCharges: "Càrrecs de reserves",
        usersRoles: "Usuaris i rols",
        userExperience: "Experiència d'usuari",
        bookingEngine: "Motor de reserves",
        pinSidebar: "Fixa la sidebar",
        unpinSidebar: "Desfixa la sidebar",
      },
      reservations: {
        pageTitle: "Reserves",
        loadedCount: (count) => `${count} reserves carregades`,
        add: "Afegeix",
        exportToExcel: "Exporta a Excel",
        viewCancelled: "Mostra cancel·lades",
        hideCancelled: "Amaga cancel·lades",
        searchPlaceholder: "Cerca per localitzador, client, habitació, dates, estat...",
        occupancyGrid: "Graella d'ocupació",
        filters: "Filtres",
        clearAllFilters: "Neteja'ls tots",
        showingRange: (start, end, total) => `Mostrant ${start}-${end} de ${total}`,
        size: "mida",
        prev: "Anterior",
        next: "Següent",
        pageCounter: (page, total) => `Pàgina ${page}/${total}`,
        locator: "Localitzador",
        externalLocator: "Localitzador extern",
        customer: "Client",
        roomCategory: "Categoria d'habitació",
        room: "Habitació",
        rate: "Tarifa",
        checkIn: "Entrada",
        checkOut: "Sortida",
        roomNights: "Nits",
        adults: "Adults",
        children: "Nens",
        babies: "Nadons",
        reservationStatus: "Estat de la reserva",
        totalPrice: "Preu total",
        paidAmount: "Import pagat",
        externalAgency: "Agència externa",
        createdAt: "Creat el",
        createdBy: "Creat per",
        loadingReservations: "Carregant reserves...",
        noReservationsFound: "No s'han trobat reserves.",
        contains: (needle) => `Conté: ${needle}`,
        all: "Tots",
        categoriesCount: (count) => `${count} categories`,
        roomsCount: (count) => `${count} habitacions`,
        ratesCount: (count) => `${count} tarifes`,
        datesCount: (count) => `${count} dates`,
        statusesCount: (count) => `${count} estats`,
        agenciesCount: (count) => `${count} agències`,
        selectedCount: (count) => `${count} seleccionats`,
        textFilterPlaceholder: "Conté...",
        textFilterHint: "Filtra per text dins del camp.",
        searchIn: (title) => `Cerca a ${String(title || "").toLocaleLowerCase("ca-ES")}...`,
        noMatchingOptions: "No hi ha opcions coincidents.",
        selectValuesHint: "Selecciona un o més valors.",
        openReservation: "Obre reserva",
        detailsPermissionRequired: "Cal el permís Veure detalls de la reserva",
        detailsPermissionAlert:
          "Accés denegat: necessites el permís Veure detalls de la reserva per obrir aquesta entrada.",
        errorCreatingGroup: "Error en crear el grup i la primera reserva.",
        noDataToExport: "No hi ha dades per exportar.",
        groupRatePrefix: "Tarifa de grup",
        booker: "titular",
        workbookSheetName: "Reserves",
        workbookFileName: "reserves.xlsx",
      },
      reservationsTabs: {
        noPermission: "No tens permís per veure reserves.",
        savedSuccess: "Reserva desada correctament",
        listOfReservations: "Llista de reserves",
        waitlist: "Llista d'espera",
        reservationsAvailability: "Disponibilitat de reserves",
        categorySalesControl: "Control de vendes per categoria",
      },
      statuses: {
        confirmed: "Confirmada",
        requested: "Sol·licitada",
        cancelled: "Cancel·lada",
        canceled: "Cancel·lada",
        checked_in: "Check-in fet",
        checked_out: "Check-out fet",
        no_show: "No show",
        in_house: "Allotjada",
        draft: "Esborrany",
      },
    },
  },
];

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function getUserPreferenceMap() {
  if (typeof window === "undefined") return {};

  try {
    const raw = JSON.parse(localStorage.getItem(PMS_LANGUAGE_BY_USER_STORAGE_KEY) || "{}");
    return isPlainObject(raw) ? raw : {};
  } catch (_err) {
    return {};
  }
}

function setUserPreferenceMap(nextMap) {
  if (typeof window === "undefined") return;

  try {
    localStorage.setItem(PMS_LANGUAGE_BY_USER_STORAGE_KEY, JSON.stringify(nextMap));
  } catch (_err) {
    // Ignore storage failures and keep the language in memory only.
  }
}

function getUserCacheKey(user) {
  const rawUserId = user?.id;
  if (rawUserId == null) return "";
  return String(rawUserId).trim();
}

export const PMS_LANGUAGE_OPTIONS = Object.freeze(
  languages.map(({ id, label, nativeLabel, locale }) => ({ id, label, nativeLabel, locale }))
);

const languageMap = languages.reduce((acc, language) => {
  acc[language.id] = language;
  return acc;
}, {});

export function isKnownPmsLanguageId(languageId) {
  return Boolean(languageMap[String(languageId || "")]);
}

export function getPmsLanguageDefinition(languageId) {
  return languageMap[String(languageId || "")] || languageMap[PMS_DEFAULT_LANGUAGE_ID];
}

export function readPmsLanguageIdFromUser(user) {
  const preferences = isPlainObject(user?.preferences) ? user.preferences : {};
  const uiPreferences = isPlainObject(user?.ui_preferences) ? user.ui_preferences : {};
  const candidates = [
    user?.pms_language_id,
    preferences.pms_language_id,
    preferences.pmsLanguageId,
    uiPreferences.pms_language_id,
    uiPreferences.pmsLanguageId,
  ];

  const knownLanguageId = candidates.find((candidate) => isKnownPmsLanguageId(candidate));
  return knownLanguageId || "";
}

export function storePmsLanguageId(languageId, user = null) {
  const safeLanguageId = isKnownPmsLanguageId(languageId)
    ? String(languageId)
    : PMS_DEFAULT_LANGUAGE_ID;
  if (typeof window === "undefined") return safeLanguageId;

  try {
    localStorage.setItem(PMS_LANGUAGE_STORAGE_KEY, safeLanguageId);
  } catch (_err) {
    // Ignore storage failures and keep the language in memory only.
  }

  const userKey = getUserCacheKey(user);
  if (userKey) {
    const currentMap = getUserPreferenceMap();
    setUserPreferenceMap({
      ...currentMap,
      [userKey]: safeLanguageId,
    });
  }

  return safeLanguageId;
}

export function readStoredPmsLanguageId(user = null) {
  const userLanguageId = readPmsLanguageIdFromUser(user);
  if (userLanguageId) return userLanguageId;

  const userKey = getUserCacheKey(user);
  if (userKey) {
    const userMap = getUserPreferenceMap();
    return isKnownPmsLanguageId(userMap[userKey]) ? userMap[userKey] : PMS_DEFAULT_LANGUAGE_ID;
  }

  if (typeof window === "undefined") return PMS_DEFAULT_LANGUAGE_ID;

  try {
    const storedLanguageId = localStorage.getItem(PMS_LANGUAGE_STORAGE_KEY);
    return isKnownPmsLanguageId(storedLanguageId) ? storedLanguageId : PMS_DEFAULT_LANGUAGE_ID;
  } catch (_err) {
    return PMS_DEFAULT_LANGUAGE_ID;
  }
}

const normalizeStatusKey = (status) =>
  String(status || "")
    .trim()
    .toLowerCase()
    .replace(/[()/]/g, " ")
    .replace(/[\s-]+/g, "_");

export function translateReservationStatus(status, languageId) {
  const language = getPmsLanguageDefinition(languageId);
  const normalizedStatus = normalizeStatusKey(status);
  return language?.messages?.statuses?.[normalizedStatus] || String(status || "");
}
